import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import UpdateChecker from "@/components/UpdateChecker";
import { 
  getSettings,
  updateSettings,
  getDatabaseStats,
  getTemplateStats,
  exportTemplates,
  importTemplates
} from "@/lib/api";
import { 
  Settings, 
  User, 
  Shield, 
  Database, 
  FileText, 
  Bell, 
  Palette,
  Download,
  Upload,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  ExternalLink,
  Pill,
  BookOpen
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { UserSettings } from "@/types/prescription";
import { cn } from "@/lib/utils";

// Template stats interface
interface TemplateStats {
  total: number;
  byType: {
    medicine: number;
    advice: number;
    investigation: number;
    complaint: number;
    diagnosis: number;
    quick_notes: number;
  };
}

export default function SettingsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Settings | OneCheckUp"; }, []);

  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(false);
  const [templateStats, setTemplateStats] = useState<TemplateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  // Settings state
  const [settings, setSettings] = useState<UserSettings>({
    profile: {
      name: "",
      email: "",
      phone: "",
      specialty: "General Medicine",
      license: "",
      signature: ""
    },
    clinic: {
      name: "",
      address: "",
      phone: "",
      email: ""
    },
    preferences: {
      theme: "light",
      language: "en",
      notifications: true,
      autoSave: true,
      printFormat: "a4"
    }
  });

  // Load settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
      
      try {
        const settingsData = await getSettings(currentUser.doctor_id, currentUser.clinic_id);
        if (settingsData) {
          setSettings(settingsData);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Don't show error for first-time users - settings will be defaults
        console.log('Using default settings for first-time user');
      }
    };

    fetchSettings();
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  // Load template stats when templates tab is active, and database stats for backup tab
  useEffect(() => {
    if (activeTab === 'templates' && currentUser?.doctor_id && currentUser?.clinic_id) {
      const fetchStats = async () => {
        try {
          setStatsLoading(true);
          const stats = await getTemplateStats(currentUser.doctor_id, currentUser.clinic_id);
          setTemplateStats(stats);
        } catch (error) {
          console.error('Failed to fetch template stats:', error);
          setTemplateStats({
            total: 0,
            byType: {
              medicine: 0,
              advice: 0,
              investigation: 0,
              complaint: 0,
              diagnosis: 0,
              quick_notes: 0
            }
          });
        } finally {
          setStatsLoading(false);
        }
      };
      fetchStats();
    }

    // Also fetch database stats for backup tab
    if (activeTab === 'backup' && currentUser?.doctor_id && currentUser?.clinic_id) {
      const fetchDatabaseStats = async () => {
        try {
          setStatsLoading(true);
          const dbStats = await getDatabaseStats(currentUser.doctor_id, currentUser.clinic_id);
          // Merge with template stats
          setTemplateStats(prev => ({
            ...prev,
            ...dbStats
          }));
        } catch (error) {
          console.error('Failed to fetch database stats:', error);
        } finally {
          setStatsLoading(false);
        }
      };
      fetchDatabaseStats();
    }
  }, [activeTab, currentUser?.doctor_id, currentUser?.clinic_id]);

  // Clear notification after 4 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
  };

  const handleSave = async (section: keyof UserSettings) => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;

    setIsLoading(true);
    try {
      // Flatten settings and sanitize payload
      const flatSettings = {
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id,
        profile_name: settings.profile.name,
        profile_email: settings.profile.email,
        profile_phone: settings.profile.phone,
        profile_specialty: settings.profile.specialty,
        profile_license: settings.profile.license,
        profile_signature: settings.profile.signature,
        clinic_name: settings.clinic.name,
        clinic_address: settings.clinic.address,
        clinic_phone: settings.clinic.phone,
        clinic_email: settings.clinic.email,
        theme: settings.preferences.theme,
        language: settings.preferences.language,
        notifications: typeof settings.preferences.notifications === 'boolean' ? settings.preferences.notifications : !!settings.preferences.notifications,
        auto_save: typeof settings.preferences.autoSave === 'boolean' ? settings.preferences.autoSave : !!settings.preferences.autoSave,
        print_format: settings.preferences.printFormat || settings.preferences.print_format || 'a4',
      };
      // Only send the fields backend expects
      console.log('Saving settings (flattened):', flatSettings);
      const updatedSettings = await updateSettings(currentUser.doctor_id, currentUser.clinic_id, flatSettings);
      if (updatedSettings?.settings) {
        setSettings(updatedSettings.settings);
      }
      showNotification('success', `${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully`);
    } catch (error) {
      console.error(`Failed to save ${section} settings:`, error);
      showNotification('error', `Failed to save ${section} settings`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackup = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;

    setIsLoading(true);
    try {
      // Create comprehensive backup data
      const backupData = {
        timestamp: new Date().toISOString(),
        version: "1.0",
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id,
        data: {
          settings,
          // In production, you'd fetch actual data from your APIs
          // templates: await getAllTemplates(currentUser.doctor_id, currentUser.clinic_id),
          // patients: await getPatients(),
          // prescriptions: await getPrescriptions(),
          metadata: {
            export_type: "full_backup",
            app_version: "1.0.0"
          }
        }
      };
      
      const blob = new Blob([JSON.stringify(backupData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medical-app-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification('success', 'Backup downloaded successfully');
    } catch (error) {
      console.error('Failed to create backup:', error);
      showNotification('error', 'Failed to create backup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateExport = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    
    try {
      setIsLoading(true);
      const result = await exportTemplates(currentUser.doctor_id, currentUser.clinic_id);
      if (result?.success) {
        showNotification('success', `Successfully exported ${result.count} templates`);
      }
    } catch (error) {
      console.error('Failed to export templates:', error);
      showNotification('error', 'Failed to export templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateImport = async (file: File) => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    
    try {
      setIsLoading(true);
      const result = await importTemplates(file, currentUser.doctor_id, currentUser.clinic_id) as any;
      showNotification('success', `Imported ${result.imported} of ${result.total} templates`);
      
      // Refresh template stats if on templates tab
      if (activeTab === 'templates') {
        const stats = await getTemplateStats(currentUser.doctor_id, currentUser.clinic_id);
        setTemplateStats(stats);
      }
    } catch (error) {
      console.error('Failed to import templates:', error);
      showNotification('error', 'Failed to import templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToTemplates = () => {
    navigate('/templates');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'backup' | 'templates') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (type === 'templates') {
      handleTemplateImport(file);
    } else {
      // Handle backup restore in production
      showNotification('error', 'Backup restore not implemented yet');
    }

    // Reset file input
    event.target.value = '';
  };

  return (
    <div className="prescription-form-container">
      {/* Header */}
      <div className="border-b border-border pb-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your profile, clinic, and application preferences</p>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <Alert className={cn(
          "mb-6",
          notification.type === 'success' ? 'border-success bg-success/10' : 'border-destructive bg-destructive/10'
        )}>
          {notification.type === 'success' ? 
            <CheckCircle className="h-4 w-4 text-success" /> : 
            <AlertTriangle className="h-4 w-4 text-destructive" />
          }
          <AlertDescription className={notification.type === 'success' ? 'text-success' : 'text-destructive'}>
            {notification.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="clinic" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Clinic
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="updates" className="flex items-center gap-2 hidden">
            <Download className="w-4 h-4" />
            Updates
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Data
          </TabsTrigger>
        </TabsList>

        {/* Profile Settings */}
        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Doctor Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="doctor-name">Full Name *</Label>
                  <Input 
                    id="doctor-name" 
                    value={settings.profile.name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, name: e.target.value }
                    }))}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="specialty">Specialty *</Label>
                  <Select 
                    id="specialty"
                    value={settings.profile.specialty}
                    onValueChange={(value) => setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, specialty: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Medicine">General Medicine</SelectItem>
                      <SelectItem value="Cardiology">Cardiology</SelectItem>
                      <SelectItem value="Dermatology">Dermatology</SelectItem>
                      <SelectItem value="Pediatrics">Pediatrics</SelectItem>
                      <SelectItem value="Orthopedics">Orthopedics</SelectItem>
                      <SelectItem value="Gynecology">Gynecology</SelectItem>
                      <SelectItem value="ENT">ENT</SelectItem>
                      <SelectItem value="Neurology">Neurology</SelectItem>
                      <SelectItem value="Psychiatry">Psychiatry</SelectItem>
                      <SelectItem value="Ophthalmology">Ophthalmology</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input 
                    id="email" 
                    type="email"
                    value={settings.profile.email}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, email: e.target.value }
                    }))}
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input 
                    id="phone" 
                    value={settings.profile.phone}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, phone: e.target.value }
                    }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="license">Medical License Number</Label>
                  <Input 
                    id="license" 
                    value={settings.profile.license}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, license: e.target.value }
                    }))}
                    placeholder="Enter your medical license number"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signature">Digital Signature</Label>
                <Textarea 
                  id="signature"
                  placeholder="Enter your digital signature text that will appear on prescriptions..."
                  value={settings.profile.signature}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    profile: { ...prev.profile, signature: e.target.value }
                  }))}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  This signature will be included in printed prescriptions and reports.
                </p>
              </div>
              
              <Button 
                onClick={() => handleSave('profile')} 
                disabled={isLoading || !settings.profile.name || !settings.profile.email}
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinic Settings */}
        <TabsContent value="clinic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Clinic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clinic-name">Clinic Name *</Label>
                  <Input 
                    id="clinic-name" 
                    value={settings.clinic.name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: { ...prev.clinic, name: e.target.value }
                    }))}
                    placeholder="Enter clinic name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="clinic-phone">Phone</Label>
                  <Input 
                    id="clinic-phone" 
                    value={settings.clinic.phone}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: { ...prev.clinic, phone: e.target.value }
                    }))}
                    placeholder="+91 22 1234 5678"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="clinic-email">Email</Label>
                  <Input 
                    id="clinic-email" 
                    type="email"
                    value={settings.clinic.email}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      clinic: { ...prev.clinic, email: e.target.value }
                    }))}
                    placeholder="clinic@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="clinic-address">Address *</Label>
                <Textarea 
                  id="clinic-address"
                  value={settings.clinic.address}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    clinic: { ...prev.clinic, address: e.target.value }
                  }))}
                  rows={3}
                  placeholder="Enter complete clinic address including city, state, and postal code"
                />
              </div>
              
              <Button 
                onClick={() => handleSave('clinic')} 
                disabled={isLoading || !settings.clinic.name || !settings.clinic.address}
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Clinic Info
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences */}
        <TabsContent value="preferences" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Application Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Theme Settings */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Theme</Label>
                  <p className="text-sm text-muted-foreground">Choose your preferred color theme</p>
                </div>
                <Select 
                  value={settings.preferences.theme}
                  onValueChange={(value: 'light' | 'dark' | 'system') => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, theme: value }
                  }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Language Settings */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Language</Label>
                  <p className="text-sm text-muted-foreground">Application display language</p>
                </div>
                <Select 
                  value={settings.preferences.language}
                  onValueChange={(value) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, language: value }
                  }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">हिन्दी</SelectItem>
                    <SelectItem value="mr">मराठी</SelectItem>
                    <SelectItem value="ta">தமிழ்</SelectItem>
                    <SelectItem value="te">తెలుగు</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Auto-save */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Auto-save</Label>
                  <p className="text-sm text-muted-foreground">Automatically save form data while typing</p>
                </div>
                <Switch 
                  checked={settings.preferences.autoSave}
                  onCheckedChange={(checked) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, autoSave: checked }
                  }))}
                />
              </div>
              
              {/* Notifications */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive app notifications and reminders</p>
                </div>
                <Switch 
                  checked={settings.preferences.notifications}
                  onCheckedChange={(checked) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, notifications: checked }
                  }))}
                />
              </div>
              
              {/* Print Format */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Print Format</Label>
                  <p className="text-sm text-muted-foreground">Default paper format for prescriptions</p>
                </div>
                <Select 
                  value={settings.preferences.printFormat}
                  onValueChange={(value: 'a4' | 'letter' | 'compact') => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, printFormat: value }
                  }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4 (210×297mm)</SelectItem>
                    <SelectItem value="letter">Letter (8.5×11")</SelectItem>
                    <SelectItem value="compact">Compact</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={() => handleSave('preferences')} disabled={isLoading}>
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Software Updates */}
        <TabsContent value="updates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Software Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UpdateChecker />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Template Management */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Template Management
              </CardTitle>
            </CardHeader>
            <CardContent className="py-12">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Coming Soon</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Template management functionality is currently under development. 
                  This feature will allow you to create, manage, and share reusable templates for medicines, advice, and investigations.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup & Data */}
        <TabsContent value="backup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Backup & Restore
              </CardTitle>
            </CardHeader>
            <CardContent className="py-12">
              <div className="text-center">
                <Database className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Coming Soon</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Backup and restore functionality is currently under development. 
                  This feature will allow you to safely backup and restore your clinic data.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}